import { useState } from "react";

import { Calendar } from "../../assets/icons/Calendar";
import { GraphToggle } from "../../assets/icons/GraphToggle";
import { TableToggle } from "../../assets/icons/TableToggle";
import { css, styled } from "../../styles";
import { OptionButton, OptionsBar } from "../OptionsBar";
import { CompareDropdown } from "./CompareDropdown";

const Container = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "7px",
  alignItems: "center",
});

const SvgCss = css({
  svg: {
    margin: "0px !important",
    width: "auto !important",
  },
});

const CompareWrapper = styled("div", {
  position: "relative",
});

const CompareButton = styled("button", {
  fontFamily: "$openSans",
  fontSize: "$text-sm",
  fontWeight: 600,
  lineHeight: "16px",

  height: "24px",
  padding: "0 10px",
  borderRadius: "8px",
  border: "1px solid $gray-50",
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  variants: {
    active: {
      true: {
        backgroundColor: "#E8E8F4",
        color: "#3B3F8C",
        borderColor: "#C8C8E8",
      },
      false: {
        backgroundColor: "white",
        color: "$gray-80",
      },
    },
  },

  "&:hover": {
    backgroundColor: "$gray-30",
  },
});

const DATE_OPTIONS = ["Custom", "48H", "7D", "1M", "6M", "12M", "ALL"];

type DualAxisOptionsBarProps = {
  onDateFilterChange?: (label: string) => void;
  comparedKeys: string[];
  lockedUnit: string | null;
  onToggleCompare: (key: string) => void;
};

export function DualAxisOptionsBar({
  onDateFilterChange,
  comparedKeys,
  lockedUnit,
  onToggleCompare,
}: DualAxisOptionsBarProps) {
  const [selectedDate, setSelectedDate] = useState("12M");
  const [showGraph, setShowGraph] = useState(true);
  const [compareOpen, setCompareOpen] = useState(false);

  const compareCount = comparedKeys.length;
  const compareLabel =
    compareCount > 0 ? `Compare (${compareCount})` : "Compare";

  return (
    <Container>
      <OptionsBar>
        {DATE_OPTIONS.map((label) => (
          <OptionButton
            key={label}
            text={label}
            icon={label === "Custom" ? <Calendar /> : undefined}
            selected={selectedDate === label}
            onClick={() => {
              setSelectedDate(label);
              onDateFilterChange?.(label);
            }}
          />
        ))}
      </OptionsBar>

      <OptionsBar>
        <OptionButton
          text=""
          className={SvgCss()}
          selected={showGraph}
          onClick={() => setShowGraph(true)}
          icon={<GraphToggle />}
        />
        <OptionButton
          text=""
          className={SvgCss()}
          selected={!showGraph}
          onClick={() => setShowGraph(false)}
          icon={<TableToggle />}
        />
      </OptionsBar>

      <CompareWrapper>
        <CompareButton
          active={compareCount > 0}
          onClick={() => setCompareOpen((prev) => !prev)}
        >
          {compareLabel}
        </CompareButton>
        {compareOpen && (
          <CompareDropdown
            selectedKeys={comparedKeys}
            lockedUnit={lockedUnit}
            onToggle={onToggleCompare}
            onClose={() => setCompareOpen(false)}
          />
        )}
      </CompareWrapper>
    </Container>
  );
}
